import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { newPasswordSchema } from "@/contracts";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { AuthBrandLayout } from "./auth-brand-layout";

const formSchema = z
  .object({
    password: newPasswordSchema,
    confirm: z.string().min(1),
  })
  .refine((value) => value.password === value.confirm, {
    message: "Passwords do not match.",
    path: ["confirm"],
  });

type FormValues = z.infer<typeof formSchema>;

export function ResetPasswordPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token")?.trim() ?? "";
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${t("resetHeading")} · ZonaClara`;
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "", confirm: "" },
  });

  if (!token) {
    return (
      <AuthBrandLayout title={t("resetHeading")} subtitle={t("resetMissingToken")}>
        <div className="rounded-lg border border-border-subtle bg-surface p-6 shadow-sm">
          <Link to="/forgot-password" className="text-sm font-medium text-brand-strong hover:underline">
            {t("resetRequestNewLink")}
          </Link>
        </div>
      </AuthBrandLayout>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await client.confirmPasswordReset({ token, password: values.password });
      void navigate("/login", { replace: true, state: { passwordReset: true } });
    } catch (error) {
      if (error instanceof ApiError && error.code === "UNAUTHENTICATED") {
        setFormError(t("resetInvalidLink"));
        return;
      }

      if (error instanceof ApiError && error.code === "RATE_LIMITED") {
        setFormError(t("resetRateLimited"));
        return;
      }

      setFormError(error instanceof ApiError ? error.message : t("resetFailed"));
    }
  });

  return (
    <AuthBrandLayout title={t("resetHeading")} subtitle={t("resetTagline")}>
      <form
        onSubmit={(event) => void onSubmit(event)}
        noValidate
        className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface p-6 shadow-sm"
      >
        {formError ? <Alert>{formError}</Alert> : null}

        <TextField
          label={t("resetPassword")}
          type="password"
          autoComplete="new-password"
          required
          hint={t("resetPasswordHint")}
          {...(errors.password?.message === undefined ? {} : { error: errors.password.message })}
          {...register("password")}
        />

        <TextField
          label={t("resetConfirm")}
          type="password"
          autoComplete="new-password"
          required
          {...(errors.confirm?.message === undefined
            ? {}
            : { error: errors.confirm.message === "Passwords do not match." ? t("resetMismatch") : errors.confirm.message })}
          {...register("confirm")}
        />

        <Button type="submit" loading={isSubmitting}>
          {t("resetSubmit")}
        </Button>

        <Link to="/login" className="text-sm font-medium text-brand-strong hover:underline">
          {t("forgotBackToSignIn")}
        </Link>
      </form>
    </AuthBrandLayout>
  );
}
